import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import AdminPage from '../../components/admin/AdminPage';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import MetricCard from '../../components/admin/MetricCard';
import SectionCard from '../../components/admin/SectionCard';
import { ErrorState, LoadingState } from '../../components/admin/StateViews';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../context/ToastContext';
import { AuditLogService } from '../../services/AuditLogService';
import { CircleService } from '../../services/CircleService';
import { AdminCircleActivity, AdminCircleDetail } from '../../types';

interface CircleFormValues {
  name: string;
  description: string;
  coverPhotoUrl: string;
  coverPhotoStorageKey: string;
  isPublic: boolean;
  isFeatured: boolean;
}

const formatDate = (value?: string | null) =>
  value
    ? new Date(value).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : 'Not set';

const formatNumber = (value: number) =>
  new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
  }).format(value || 0);

const toFormValues = (circle: AdminCircleDetail): CircleFormValues => ({
  name: circle.name,
  description: circle.description,
  coverPhotoUrl: circle.coverPhotoUrl || '',
  coverPhotoStorageKey: circle.coverPhotoStorageKey || '',
  isPublic: Boolean(circle.isPublic ?? true),
  isFeatured: Boolean(circle.isFeatured),
});

const readFileAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
    reader.readAsDataURL(file);
  });

const CircleDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const canEdit = ['SuperAdmin', 'Content-Manager'].includes(user?.role || '');
  const canDelete = user?.role === 'SuperAdmin';
  const [circle, setCircle] = useState<AdminCircleDetail | null>(null);
  const [values, setValues] = useState<CircleFormValues>({
    name: '',
    description: '',
    coverPhotoUrl: '',
    coverPhotoStorageKey: '',
    isPublic: true,
    isFeatured: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [adjustingPoints, setAdjustingPoints] = useState(false);
  const [uploadingCoverPhoto, setUploadingCoverPhoto] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const coverPhotoInputRef = useRef<HTMLInputElement | null>(null);

  const loadCircle = useCallback(async () => {
    if (!id) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await CircleService.getCircleById(id);
      setCircle(result);
      setValues(toFormValues(result));
    } catch (loadError) {
      console.error(loadError);
      setError('Failed to load circle details.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadCircle();
  }, [loadCircle]);

  const handleSave = async () => {
    if (!id || !circle) {
      return;
    }

    if (!values.name.trim()) {
      setValidationError('Circle name is required.');
      return;
    }

    if (!values.description.trim()) {
      setValidationError('Circle description is required.');
      return;
    }

    try {
      setSaving(true);
      setValidationError(null);
      const updated = await CircleService.updateCircle(id, {
        name: values.name.trim(),
        description: values.description.trim(),
        coverPhotoUrl: values.coverPhotoUrl.trim(),
        coverPhotoStorageKey: values.coverPhotoStorageKey.trim(),
        isPublic: values.isPublic,
        isFeatured: values.isFeatured,
      });
      setCircle(updated);
      setValues(toFormValues(updated));
      showToast('Circle updated.');
      await AuditLogService.recordEntry({
        module: 'circles',
        action: 'updated',
        description: `${updated.name} was updated from the admin circles page.`,
        actorEmail: user?.email || 'admin@local',
        targetId: updated.id,
        targetLabel: updated.name,
      });
    } catch (saveError) {
      console.error(saveError);
      setValidationError('Failed to save circle changes.');
    } finally {
      setSaving(false);
    }
  };

  const handleCoverPhotoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    try {
      setUploadingCoverPhoto(true);
      setValidationError(null);
      const dataUrl = await readFileAsDataUrl(file);
      const uploaded = await CircleService.uploadCoverPhoto({
        dataUrl,
        fileName: file.name,
      });
      setValues((current) => ({
        ...current,
        coverPhotoUrl: uploaded.url,
        coverPhotoStorageKey: uploaded.storageKey,
      }));
      showToast('Cover photo uploaded.');
    } catch (uploadError) {
      console.error(uploadError);
      setValidationError('Failed to upload the cover photo.');
    } finally {
      setUploadingCoverPhoto(false);
      event.target.value = '';
    }
  };

  const handleDelete = async () => {
    if (!id || !circle) {
      return;
    }

    try {
      await CircleService.deleteCircle(id);
      showToast('Circle deleted.');
      await AuditLogService.recordEntry({
        module: 'circles',
        action: 'deleted',
        description: `${circle.name} was deleted from the admin circle details page.`,
        actorEmail: user?.email || 'admin@local',
        targetId: circle.id,
        targetLabel: circle.name,
      });
      navigate('/circles');
    } catch (deleteError) {
      console.error(deleteError);
      setValidationError('Failed to delete this circle.');
    } finally {
      setConfirmDeleteOpen(false);
    }
  };

  const handleAdjustPoints = async () => {
    if (!id || !circle) {
      return;
    }

    const pointsInput = window.prompt(
      'Enter points adjustment. Use a negative number to subtract points.',
      '0',
    );
    if (pointsInput === null) {
      return;
    }

    const points = Number(pointsInput);
    if (!Number.isInteger(points) || points === 0) {
      setValidationError('Enter a non-zero whole number of points.');
      return;
    }

    const reason = window.prompt('Reason for this adjustment');
    if (reason === null || reason.trim().length < 3) {
      setValidationError('Enter a reason of at least 3 characters.');
      return;
    }

    try {
      setAdjustingPoints(true);
      setValidationError(null);
      const updated = await CircleService.adjustPoints(id, {
        points,
        reason: reason.trim(),
      });
      setCircle(updated);
      setValues(toFormValues(updated));
      showToast('Circle points adjusted.');
      await AuditLogService.recordEntry({
        module: 'circles',
        action: 'points_adjusted',
        description: `${circle.name} points adjusted by ${points}.`,
        actorEmail: user?.email || 'admin@local',
        targetId: circle.id,
        targetLabel: circle.name,
      });
    } catch (adjustError) {
      console.error(adjustError);
      setValidationError('Failed to adjust circle points.');
    } finally {
      setAdjustingPoints(false);
    }
  };

  const replaceActivity = (activity: AdminCircleActivity) => {
    setCircle((current) =>
      current
        ? {
            ...current,
            activities: current.activities.map((item) =>
              item.id === activity.id ? activity : item,
            ),
          }
        : current,
    );
  };

  const handleEditActivity = async (activity: AdminCircleActivity) => {
    if (!id) {
      return;
    }

    const message = window.prompt('Edit activity message', activity.message);
    if (message === null || !message.trim() || message.trim() === activity.message) {
      return;
    }

    try {
      replaceActivity(await CircleService.updateActivity(id, activity.id, message.trim()));
      showToast('Activity updated.');
    } catch (editError) {
      console.error(editError);
      setValidationError('Failed to update activity.');
    }
  };

  const handleDeleteActivity = async (activity: AdminCircleActivity) => {
    if (!id || !window.confirm('Delete this activity and its comments?')) {
      return;
    }

    try {
      await CircleService.deleteActivity(id, activity.id);
      setCircle((current) =>
        current
          ? {
              ...current,
              activities: current.activities.filter((item) => item.id !== activity.id),
            }
          : current,
      );
      showToast('Activity deleted.');
    } catch (deleteError) {
      console.error(deleteError);
      setValidationError('Failed to delete activity.');
    }
  };

  const handleEditComment = async (
    activity: AdminCircleActivity,
    commentId: string,
    currentBody: string,
  ) => {
    if (!id) {
      return;
    }

    const body = window.prompt('Edit comment', currentBody);
    if (body === null || !body.trim() || body.trim() === currentBody) {
      return;
    }

    try {
      replaceActivity(
        await CircleService.updateActivityComment(
          id,
          activity.id,
          commentId,
          body.trim(),
        ),
      );
      showToast('Comment updated.');
    } catch (editError) {
      console.error(editError);
      setValidationError('Failed to update comment.');
    }
  };

  const handleDeleteComment = async (
    activity: AdminCircleActivity,
    commentId: string,
  ) => {
    if (!id || !window.confirm('Delete this comment?')) {
      return;
    }

    try {
      await CircleService.deleteActivityComment(id, activity.id, commentId);
      setCircle((current) =>
        current
          ? {
              ...current,
              activities: current.activities.map((item) =>
                item.id === activity.id
                  ? {
                      ...item,
                      commentCount: Math.max(0, item.commentCount - 1),
                      comments: item.comments.filter(
                        (comment) => comment.id !== commentId,
                      ),
                    }
                  : item,
              ),
            }
          : current,
      );
      showToast('Comment deleted.');
    } catch (deleteError) {
      console.error(deleteError);
      setValidationError('Failed to delete comment.');
    }
  };

  if (loading) {
    return <LoadingState label="Loading circle details…" />;
  }

  if (error || !circle) {
    return <ErrorState message={error || 'Circle not found.'} onRetry={loadCircle} />;
  }

  return (
    <AdminPage
      title={circle.name}
      description={circle.description}
      action={
        <Stack direction="row" spacing={1.5}>
          {canEdit ? (
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          ) : null}
          {canDelete && !circle.deletedAt ? (
            <Button
              variant="outlined"
              color="error"
              onClick={() => setConfirmDeleteOpen(true)}
            >
              Delete circle
            </Button>
          ) : null}
        </Stack>
      }
    >
      {circle.deletedAt ? (
        <Alert severity="warning">
          This circle has been deleted. Historical circle-specific data is no longer active.
        </Alert>
      ) : null}
      {validationError ? <Alert severity="error">{validationError}</Alert> : null}

      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            label="Total points"
            value={formatNumber(circle.totalPoints)}
            helperText="Sum of circle point transactions"
          />
          {canEdit && !circle.deletedAt ? (
            <Button
              sx={{ mt: 1 }}
              size="small"
              variant="outlined"
              onClick={handleAdjustPoints}
              disabled={adjustingPoints}
            >
              {adjustingPoints ? 'Adjusting...' : 'Adjust points'}
            </Button>
          ) : null}
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            label="Members"
            value={formatNumber(circle.memberCount)}
            helperText="Active members in this circle"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            label="Tracked donation amount"
            value={formatNumber(circle.totalAmountRaised)}
            helperText="Raw total from circle point transactions"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            label="Created"
            value={formatDate(circle.createdAt)}
            helperText={circle.creatorEmail || circle.creator.displayName}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={7}>
          <SectionCard
            title="Circle summary"
            description="Review circle ownership, timestamps, and editable profile fields."
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Creator
                </Typography>
                <Typography>{circle.creator.displayName}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Creator email
                </Typography>
                <Typography>{circle.creatorEmail || 'Not available'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Created at
                </Typography>
                <Typography>{formatDate(circle.createdAt)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Updated at
                </Typography>
                <Typography>{formatDate(circle.updatedAt)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Visibility
                </Typography>
                <Typography>{circle.isPublic ? 'Public preview' : 'Hidden from discovery'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Featured
                </Typography>
                <Typography>{circle.isFeatured ? 'Featured first' : 'Standard order'}</Typography>
              </Grid>
            </Grid>

            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Circle name"
                value={values.name}
                onChange={(event) =>
                  setValues((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                disabled={!canEdit || Boolean(circle.deletedAt)}
              />
              <TextField
                fullWidth
                label="Description"
                value={values.description}
                multiline
                minRows={4}
                onChange={(event) =>
                  setValues((current) => ({
                    ...current,
                    description: event.target.value,
                  }))
                }
                disabled={!canEdit || Boolean(circle.deletedAt)}
              />
              <TextField
                fullWidth
                label="Cover photo URL or data URL"
                value={values.coverPhotoUrl}
                onChange={(event) =>
                  setValues((current) => ({
                    ...current,
                    coverPhotoUrl: event.target.value,
                    coverPhotoStorageKey: '',
                  }))
                }
                disabled={!canEdit || Boolean(circle.deletedAt)}
              />
              {canEdit && !circle.deletedAt ? (
                <Stack direction="row" spacing={1.5}>
                  <Button
                    variant="outlined"
                    onClick={() => coverPhotoInputRef.current?.click()}
                    disabled={uploadingCoverPhoto}
                  >
                    {uploadingCoverPhoto ? 'Uploading…' : 'Upload cover photo'}
                  </Button>
                  <input
                    ref={coverPhotoInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleCoverPhotoUpload}
                  />
                </Stack>
              ) : null}
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.isPublic}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          isPublic: event.target.checked,
                        }))
                      }
                      disabled={!canEdit || Boolean(circle.deletedAt)}
                    />
                  }
                  label="Visible in app discovery"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.isFeatured}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          isFeatured: event.target.checked,
                        }))
                      }
                      disabled={!canEdit || Boolean(circle.deletedAt)}
                    />
                  }
                  label="Featured priority"
                />
              </Stack>
            </Stack>
          </SectionCard>
        </Grid>

        <Grid item xs={12} lg={5}>
          <SectionCard
            title="Cover photo"
            description="Preview the cover image currently stored for the circle."
          >
            {values.coverPhotoUrl ? (
              <Box
                component="img"
                src={values.coverPhotoUrl}
                alt={circle.name}
                sx={{
                  width: '100%',
                  borderRadius: 3,
                  maxHeight: 280,
                  objectFit: 'cover',
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                }}
              />
            ) : (
              <Typography color="text.secondary">
                No cover photo is set for this circle.
              </Typography>
            )}
          </SectionCard>
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={6}>
          <SectionCard
            title="Members"
            description="Active members and their cumulative contribution totals."
          >
            <Box sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Member</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell align="right">Points</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Joined</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {circle.members.map((member) => (
                    <TableRow key={member.userId}>
                      <TableCell>{member.displayName}</TableCell>
                      <TableCell>{member.role}</TableCell>
                      <TableCell align="right">
                        {formatNumber(member.totalContributedPoints)}
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(member.totalContributedAmount)}
                      </TableCell>
                      <TableCell>{formatDate(member.joinedAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </SectionCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <SectionCard
            title="Leaderboard"
            description="Ranked contributors within this circle."
          >
            <Box sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Rank</TableCell>
                    <TableCell>Member</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell align="right">Points</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {circle.leaderboard.map((entry) => (
                    <TableRow
                      key={
                        entry.isAnonymous
                          ? `anonymous-${entry.rank}`
                          : entry.userId ?? `entry-${entry.rank}`
                      }
                    >
                      <TableCell>{entry.rank}</TableCell>
                      <TableCell>
                        {entry.isAnonymous ? 'Anonymous' : entry.displayName}
                      </TableCell>
                      <TableCell>{entry.isAnonymous ? '—' : entry.role}</TableCell>
                      <TableCell align="right">
                        {formatNumber(entry.totalContributedPoints)}
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(entry.totalContributedAmount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </SectionCard>
        </Grid>
      </Grid>

      <SectionCard
        title="Circle activities"
        description="Review member-visible activity posts, reactions, and comments."
      >
        {circle.activities.length === 0 ? (
          <Typography color="text.secondary">
            No visible circle activities yet.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {circle.activities.map((activity) => (
              <Box
                key={activity.id}
                sx={{
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Stack spacing={1.5}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography variant="subtitle1">
                        {activity.author.displayName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {activity.type} | {formatDate(activity.createdAt)}
                      </Typography>
                    </Box>
                    {canEdit ? (
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleEditActivity(activity)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => handleDeleteActivity(activity)}
                        >
                          Delete
                        </Button>
                      </Stack>
                    ) : null}
                  </Stack>
                  <Typography>{activity.message}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatNumber(activity.reactionCount)} likes |{' '}
                    {formatNumber(activity.commentCount)} comments
                  </Typography>

                  {activity.comments.length ? (
                    <Stack spacing={1.25}>
                      <Divider />
                      {activity.comments.map((comment) => (
                        <Box key={comment.id}>
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            justifyContent="space-between"
                          >
                            <Box>
                              <Typography variant="body2" fontWeight={700}>
                                {comment.author.displayName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(comment.createdAt)}
                              </Typography>
                            </Box>
                            {canEdit ? (
                              <Stack direction="row" spacing={1}>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() =>
                                    handleEditComment(
                                      activity,
                                      comment.id,
                                      comment.body,
                                    )
                                  }
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="small"
                                  variant="text"
                                  color="error"
                                  onClick={() =>
                                    handleDeleteComment(activity, comment.id)
                                  }
                                >
                                  Delete
                                </Button>
                              </Stack>
                            ) : null}
                          </Stack>
                          <Typography variant="body2">{comment.body}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  ) : null}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </SectionCard>

      <ConfirmDialog
        open={confirmDeleteOpen}
        title="Delete circle?"
        description="This will soft delete the circle and remove only circle-specific memberships, invites, default-circle references, and circle point transactions."
        confirmLabel="Delete circle"
        confirmColor="error"
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDelete}
      />
    </AdminPage>
  );
};

export default CircleDetailsPage;
